
import React, { Suspense, lazy, useRef, useEffect } from "react";
import styles from "./MainContent.module.css";
import tokenizeCard from "../../svgs/tokenizecard.svg";
import { useInView } from "react-intersection-observer";
import Layout from "./Layout";
import { CallToAction } from "./CallToAction";
import { motion } from 'framer-motion';
import BrazilAccentSVG from '../aboutUs/BrazilAccentSVG.jsx';
import { useLocale } from '../aboutUs/LocaleContext';
import { useTranslation } from 'react-i18next';
import AnimatedBrazilBackground from './AnimatedBrazilBackground';
import PixBadge from './PixBadge';
import BrasilBadge from './BrasilBadge';

const HeroSection = lazy(() => import("./HeroSection"));
const FeaturesSection = lazy(() => import("./FeaturesSection"));
const Roadmap = lazy(() => import("./Roadmap"));
const FAQ = lazy(() => import("./FAQ"));

const LazyComponent = ({ children }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  return (
    <div ref={ref} className={styles.lazyComponent}>
      {inView ? (
        <Suspense fallback={<div>Loading...</div>}>{children}</Suspense>
      ) : (
        <div>Loading...</div>
      )}
    </div>
  );
};

const MainContent = () => {
  const { locale } = useLocale();
  const { t } = useTranslation('common');
  const copy = {
    heroSub: t('pages.newsletter.heroSub'),
    heroHead: t('pages.newsletter.heroHead'),
    heroTagline: t('pages.newsletter.heroTagline'),
    signupSub: t('pages.newsletter.signupSub'),
    signupHead: t('pages.newsletter.signupHead'),
    trust: t('pages.newsletter.trust'),
    assuranceTitle: t('pages.newsletter.assuranceTitle'),
    disclaimer: t('pages.newsletter.disclaimer'),
    mockupAlt: t('pages.newsletter.mockupAlt')
  };
  // For a11y focus ring
  const mainRef = useRef();
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.addEventListener('keydown', e => {
        if (e.key === 'Tab') {
          mainRef.current.classList.add(styles.focusVisible);
        }
      });
      mainRef.current.addEventListener('mousedown', () => {
        mainRef.current.classList.remove(styles.focusVisible);
      });
    }
  }, []);
  return (
    <main
      className={styles.mainContainer}
      lang={locale}
      ref={mainRef}
      tabIndex={-1}
      aria-label={locale === 'pt-BR' ? 'Conteúdo principal do newsletter' : 'Newsletter main content'}
      style={{ position: 'relative', background: 'linear-gradient(120deg,#0C0D0E,#101F17 35%, #1E3C2E 60%, #0C0D0E)' }}
    >
      {/* Animated confetti overlay for delight */}
      <div className={styles.confetti} aria-hidden="true">
        {[...Array(18)].map((_,i) => (
          <span key={i} className={styles.confettiPiece} style={{ left: `${Math.random()*100}%`, animationDelay: `${Math.random()*3}s` }} />
        ))}
      </div>
      {/* Parallax animated Brazil SVG background */}
      <AnimatedBrazilBackground />
      {/* Floating sparkles for magic */}
      <div className={styles.sparkleOverlay} aria-hidden="true">
        {[...Array(10)].map((_,i) => (
          <span key={i} className={styles.sparkle} style={{ left: `${10+Math.random()*80}%`, top: `${10+Math.random()*60}%`, animationDelay: `${Math.random()*4}s` }} />
        ))}
      </div>
      {/* Subtle animated radial gradient for depth */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2 }} className={styles.heroRadialBg} />

      {/* HERO SECTION */}
      <motion.section className={styles.heroSection} aria-label={locale === 'pt-BR' ? 'Seção de destaque' : 'Hero section'} initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, ease: 'easeOut' }}>
        {/* Parallax Brazil SVGs for cinematic depth */}
        <motion.div
          className={styles.heroBrazilAccent}
          initial={{ opacity: 0, scale: 0.85, rotate: -10 }}
          animate={{ opacity: 0.22, scale: 1, rotate: 0, y: [0, 8, 0] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
          style={{ top: -54, left: -54 }}
        >
          <BrazilAccentSVG style={{ width: 120, height: 120 }} />
        </motion.div>
        <motion.div
          className={styles.heroBrazilAccent}
          initial={{ opacity: 0, scale: 0.85, rotate: 10 }}
          animate={{ opacity: 0.18, scale: 1, rotate: 0, y: [0, -8, 0] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }}
          style={{ bottom: -54, right: -54 }}
        >
          <BrazilAccentSVG style={{ width: 120, height: 120 }} />
        </motion.div>
        {/* Animated gradient border for hero */}
        <motion.div className={styles.heroGradientBorder} aria-hidden="true"
          animate={{ x: [0, 120, 0] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
        />
        {/* Cinematic, world-class hero content */}
        <motion.h1
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: [0.92, 1.04, 1], opacity: 1 }}
          transition={{ duration: 1.2, type: 'spring', stiffness: 120 }}
          className={styles.heading}
  >{copy.heroHead}</motion.h1>
        <motion.p
          className={styles.subHeading}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.7 }}
  >{copy.heroSub}</motion.p>
        <motion.p
          className={styles.heroTagline}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.7 }}
  >{copy.heroTagline}</motion.p>
        <motion.div className={styles.heroCtaRow} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}>
          <CallToAction subHeading={null} heading={null} />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .7, duration: .7 }} className={styles.trustPanel} aria-live="polite" style={{ margin: '32px auto 0', justifyContent: 'center' }}>
          <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            <PixBadge aria-label={locale === 'pt-BR' ? 'Preparado para PIX' : 'PIX ready'} />
            <BrasilBadge aria-label={locale === 'pt-BR' ? '100% Brasil' : '100% Brasil'} />
          </span>
          <span style={{ marginLeft: 12 }}>{copy.trust}</span>
        </motion.div>
      </motion.section>

      {/* FAQ SECTION */}
      <motion.section className={styles.faqSection} initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1.1, ease: 'easeOut', delay: 0.2 }}>
        <div className={styles.sectionCard}>
          <FAQ />
        </div>
      </motion.section>

      {/* SIGNUP SECTION */}
      <motion.section className={styles.signupSection} initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1.1, ease: 'easeOut', delay: 0.3 }}>
        <div className={styles.sectionCard}>
          <Layout>
            <div className={styles.contentContainer} style={{ justifyContent: 'center' }}>
              <motion.div initial={{ opacity: 0, y: 42 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: .85 }} style={{ position: 'relative', maxWidth: 720, textAlign: 'center' }}>
                <BrazilAccentSVG style={{ top: -24 }} />
                <CallToAction subHeading={copy.signupSub} heading={copy.signupHead} />
              </motion.div>
            </div>
          </Layout>
        </div>
      </motion.section>

      {/* ASSURANCE FOOTER SECTION */}
      <motion.footer initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: .8 }} className={styles.assuranceFooter} aria-label={locale === 'pt-BR' ? 'Painel de garantia e privacidade' : 'Assurance & privacy panel'}>
        <motion.div
          style={{ position: 'relative', width: '100%', height: 0 }}
          animate={{ y: [0, -8, 0], scale: [1, 1.04, 1] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <BrazilAccentSVG style={{ top: -18 }} />
          {/* Soft highlight sweep */}
          <motion.div
            style={{
              position: 'absolute',
              left: '30%',
              top: -10,
              width: 120,
              height: 24,
              background: 'linear-gradient(90deg,rgba(245,200,76,0.12) 0%,rgba(245,200,76,0.32) 50%,rgba(245,200,76,0.12) 100%)',
              borderRadius: 18,
              filter: 'blur(6px)',
              pointerEvents: 'none',
              zIndex: 2
            }}
            animate={{ x: [0, 120, 0] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
            aria-hidden="true"
          />
        </motion.div>
        <h3 className={styles.assuranceTitle}>{copy.assuranceTitle}</h3>
        <p className={styles.assuranceTrust}>{copy.trust}</p>
        <p className={styles.assuranceDisclaimer}>{copy.disclaimer}</p>
      </motion.footer>
  </main>
  );
};

export default MainContent;
